import React, {forwardRef, KeyboardEvent, useEffect, useImperativeHandle, useRef, useState} from 'react'

import Collection from "../../../data-structures/collection"
import GenericList from "../../../gd-modules/generic-list/render/list"
import VScroll from "../../scrolling/custom-v-scroll/v-scroll"
import EntitySelectorPane from "../content/pane"
import {useEntitySelectorContext} from "../context"
import {SortDirection} from "../../../data-structures/sort"
import EntitySelectorItem from "./item"
import EntitySelectorGlistModule from "./module"

type Props = {
    items               : EntitySelectorItem[]
    isLoading           : boolean

    titleLabel          : string
    dateLabel           : string
    noItemsMessage      : string
    dateSortDirection?  : SortDirection

    // The pick stays a prop: the tab it belongs to is the one that knows what a
    // row IS. Tab, which cycles the sidebar, is the shell's and comes off the
    // context.
    onSelect            : {(item: EntitySelectorItem): void}
}

const debug = false
// const debug = "EntitySelectorGlist"

// The shared flat list for every picker tab that has one. A tab owns its loading
// and hands the rows over -- this owns the module, the scroll and the empty
// state, so the three tabs cannot drift apart.
const EntitySelectorGlist = forwardRef((props: Props, ref) => {
    const context = useEntitySelectorContext()

    debug && console.log(debug, 'render', props.items.length)

    // No row is highlighted until the first arrow press -- see onKeyDown. A
    // fresh set of rows (a new search, a tab switch) drops back to that same
    // unhighlighted state, since the old index no longer points at what the
    // user was looking at.
    const [currentCursorKey, setCurrentCursorKey] = useState<number | null>(null)
    const scrollRef = useRef<any>()

    useEffect(() => {
        setCurrentCursorKey(null)
    }, [props.items])

    // Loading only replaces the list while there is nothing to keep on screen.
    // A reload -- every debounced keystroke is one -- swaps the rows underneath
    // instead of blanking the table and its header.
    const showLoading = props.isLoading && props.items.length === 0

    // The module owns its column widths, a gd.bus subscription and its component
    // reference, so a `new` per render would drop all three. Its data,
    // conversely, is never patched: the collection is reassigned and reprocessed
    // every pass.
    const moduleRef = useRef<EntitySelectorGlistModule | null>(null)

    // Its lifetime has to match the <GenericList> below both ways: one that is
    // never rendered leaks its subscription (`destructor()` runs on unmount
    // only), and one whose list HAS unmounted is already destructed.
    if (showLoading) {
        moduleRef.current = null
    } else if (!moduleRef.current) {
        moduleRef.current = new EntitySelectorGlistModule({
            titleLabel          : props.titleLabel,
            dateLabel           : props.dateLabel,
            noItemsMessage      : props.noItemsMessage,
            dateSortDirection   : props.dateSortDirection,
            onSelect            : props.onSelect,
        })
    }

    const module = moduleRef.current

    if (module) {
        // Closes over the tab's state, so it is refreshed every render.
        module.options.onSelect = props.onSelect

        module.data.collection = new Collection<EntitySelectorItem>(props.items)
        module.data.process()
    }

    // The rows in the order the list DISPLAYS them, which is NOT `props.items`:
    // the module sorts what it is handed (date, then title -- see `module.ts`)
    // and renders that processed copy, while the tabs hand their rows over in
    // whatever order the API answered in. A cursor walking the source array
    // moves down it while the eye follows the screen, so every row it lands on
    // is an unrelated one. Read after `process()` above -- this is the order
    // about to be painted.
    const rows: EntitySelectorItem[] = module ? module.data.get() : []

    // `setSelectedSingle` calls `module.reRender()` -> `forceUpdate()` on the
    // module's OWN internal list component -- a different mounted component
    // than this one. Doing that from inside THIS render (as this used to)
    // updates another component while React is still rendering this one, which
    // React can abort/discard -- taking the `useImperativeHandle` below down
    // with it, so the ref's `onKeyDown` never lands and the parent's calls to
    // it become silent no-ops. An effect runs after commit, so it is safe.
    useEffect(() => {
        if (!module) return

        // The module's own single-selection highlight (`.glist-row.is-selected`)
        // doubles as the keyboard cursor -- same mechanism a click-to-pick glist
        // uses elsewhere, just driven by arrow keys instead of a click.
        //
        // Asked of the module rather than closing over `rows`: `process()` hands
        // back a NEW array every render, so listing it as a dep would run this --
        // and the `reRender()` it triggers -- on every pass.
        const cursorItem = !_.isNil(currentCursorKey) ? module.data.get()[currentCursorKey] : null
        module.selection.setSelectedSingle(cursorItem ? cursorItem.id : null as unknown as ID)
    }, [module, currentCursorKey, props.items])

    const onKeyDown = (event: KeyboardEvent) => {
        let newValue

        switch (event.code) {
            case 'Tab':
                event.preventDefault()
                context.onTab()
                break

            case 'ArrowUp':
                if (!rows.length) {
                    break
                }

                newValue = _.isNil(currentCursorKey) ? 0 : Math.max(currentCursorKey - 1, 0)
                setCurrentCursorKey(newValue)
                break

            case 'ArrowDown':
                // Nothing to move through -- without this the first press would
                // take a cursor of 0 against no rows.
                if (!rows.length) {
                    break
                }

                newValue = _.isNil(currentCursorKey) ? 0 : Math.min(currentCursorKey + 1, rows.length - 1)
                setCurrentCursorKey(newValue)
                break

            case 'Enter':
                event.preventDefault()
                event.stopPropagation()

                if (!_.isNil(currentCursorKey)) {
                    const item = rows[currentCursorKey]
                    if (item) props.onSelect(item)
                }
                break
        }
    }

    useImperativeHandle(ref, () => ({
        onKeyDown,
    }))

    // Keeps the highlighted row on screen as the cursor moves past the fold --
    // mirrors `TaskFinderMain`'s own scroll-to-cursor for Browse.
    useEffect(() => {
        const node: any = scrollRef.current?.node
        const element: any = node?.querySelector('.glist-row.is-selected')
        if (element) scrollRef.current.setScrollTop(element.offsetTop + element.clientHeight/2, true, 250)
    }, [currentCursorKey])

    return (
        <EntitySelectorPane className="entity-selector-glist">
            {showLoading &&
                <div className="searching-label">
                    Loading...
                </div>
            }

            {!!module &&
                <VScroll
                    ref             = {scrollRef}
                    minDragHeight   = {20}
                >
                    <GenericList module={module}/>
                </VScroll>
            }
        </EntitySelectorPane>
    )
})

export default EntitySelectorGlist
